import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { postListConnectionsAndFollowersService } from "../services/PublicationService";
import { getUserInfoService } from "../services/UserInfoService";
import {
  getProfileImageFromUser,
  getUserId,
} from "../utils/normalizeImageSrc";
import { UserDataContext } from "../../app/contexts/Contexts";

export const USER_CONNECTIONS_CHANGED_EVENT =
  "vivanomada:user-connections-changed";

const normalizeConnectionList = (response) => {
  if (Array.isArray(response)) return response;

  if (!response || typeof response !== "object") return [];

  const possibleLists = [
    response.data,
    response.items,
    response.result,
    response.results,
    response.lista,
    response.list,
    response.usuarios,
    response.users,
    response.value,
  ];

  return possibleLists.find(Array.isArray) || [];
};

const normalizeUserInfoResponse = (response) => {
  if (!response || typeof response !== "object") return null;

  return (
    response.data ??
    response.result ??
    response.usuario ??
    response.user ??
    response.info ??
    response
  );
};

export const useConnectionsData = ({ enabled = true } = {}) => {
  const { userData } = useContext(UserDataContext);
  const [loading, setLoading] = useState(false);
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [connections, setConnections] = useState([]);
  const [error, setError] = useState(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const refreshTimeoutRef = useRef(null);
  const fetchAllInFlightRef = useRef(null);

  const enrichConnectionUser = useCallback(async (user) => {
    const userId = getUserId(user);
    if (!userId || getProfileImageFromUser(user)) return user;

    try {
      const response = await getUserInfoService(userId);
      const userInfo = normalizeUserInfoResponse(response);
      const profileImage = getProfileImageFromUser(userInfo);
      if (!profileImage) return user;

      return {
        ...user,
        ...userInfo,
        idUsuario: userId,
        nombres: user?.nombres ?? userInfo?.nombres ?? userInfo?.nombre,
        apellidos: user?.apellidos ?? userInfo?.apellidos ?? userInfo?.apellido,
        nickName: user?.nickName ?? userInfo?.nickName,
        fotoPerfilUrl: profileImage,
      };
    } catch (error) {
      console.error(`Error getting user info for ${userId}`, error);
      return user;
    }
  }, []);

  const enrichConnectionList = useCallback(
    (list) => Promise.all(list.map(enrichConnectionUser)),
    [enrichConnectionUser],
  );

  const fetchList = useCallback(
    async (listType) => {
      const req = {
        IdUsuario: userData?.idUsuario,
        Lista: listType,
      };

      const response = await postListConnectionsAndFollowersService(req);
      return enrichConnectionList(normalizeConnectionList(response));
    },
    [enrichConnectionList, userData?.idUsuario],
  );

  const fetchAll = useCallback(async () => {
    if (!enabled || !userData?.idUsuario) return;

    if (fetchAllInFlightRef.current) {
      return fetchAllInFlightRef.current;
    }

    const request = (async () => {
      setLoading(true);
      setError(null);

      try {
        const [followingResp, followersResp, connectionsResp] =
          await Promise.all([fetchList(1), fetchList(2), fetchList(3)]);

        setFollowing(followingResp);
        setFollowers(followersResp);
        setConnections(connectionsResp);
        setHasLoaded(true);
      } catch (err) {
        setError(err);
        console.error(err);
        setHasLoaded(true);
      } finally {
        setLoading(false);
      }
    })();

    fetchAllInFlightRef.current = request;

    try {
      return await request;
    } finally {
      if (fetchAllInFlightRef.current === request) {
        fetchAllInFlightRef.current = null;
      }
    }
  }, [enabled, fetchList, userData?.idUsuario]);

  const refreshSoon = useCallback(() => {
    if (!enabled) return;

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    refreshTimeoutRef.current = setTimeout(() => {
      refreshTimeoutRef.current = null;
      fetchAll();
    }, 300);
  }, [enabled, fetchAll]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (userData?.idUsuario) {
      fetchAll();
    }
  }, [enabled, fetchAll, userData?.idUsuario]);

  useEffect(() => {
    if (!enabled || !userData?.idUsuario) return;

    const handleConnectionsChange = (event) => {
      const detail = event.detail || {};
      const affectedUsers = [
        detail.IdUsuario,
        detail.idUsuario,
        detail.userId,
        detail.IdAmigo,
        detail.idAmigo,
        detail.friendId,
      ].filter(Boolean);

      if (
        affectedUsers.length > 0 &&
        !affectedUsers.some((id) => String(id) === String(userData.idUsuario))
      ) {
        return;
      }

      refreshSoon();
    };

    window.addEventListener(
      USER_CONNECTIONS_CHANGED_EVENT,
      handleConnectionsChange,
    );

    return () => {
      window.removeEventListener(
        USER_CONNECTIONS_CHANGED_EVENT,
        handleConnectionsChange,
      );
    };
  }, [enabled, refreshSoon, userData?.idUsuario]);

  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  return {
    loading,
    error,
    following,
    followers,
    connections,
    hasLoaded,
    refresh: fetchAll,
  };
};
